/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const getScopedElements = (root, selector) => {
    const elements = [];

    if (root instanceof Element && root.matches(selector)) {
      elements.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      elements.push(...root.querySelectorAll(selector));
    }

    return elements;
  };

  const getMoneyFormat = () => {
    if (!window.themeVariables?.settings) {
      return "";
    }

    return window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
  };

  const formatVariantMoney = (price) => {
    if (typeof formatMoney === "function") {
      return formatMoney(price, getMoneyFormat());
    }

    return `${(price / 100).toFixed(2)}`;
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return Number.NaN;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  };

  const initQuoteForms = (root = document) => {
    getScopedElements(root, "[data-product-quote-form]").forEach((quoteForm) => {
      if (quoteForm.dataset.purchaseModeInitialized === "true") {
        return;
      }

      const productFormId = quoteForm.getAttribute("data-product-form-id");
      const productForm = productFormId ? document.getElementById(productFormId) : null;

      if (!productForm) {
        return;
      }

      const variantIdInput = quoteForm.querySelector("[data-quote-variant-id]");
      const variantTitleInput = quoteForm.querySelector("[data-quote-variant-title]");

      productForm.addEventListener("variant:changed", (event) => {
        const variant = event.detail?.variant;

        if (variantIdInput && variant?.id) {
          variantIdInput.value = variant.id;
        }

        if (variantTitleInput && variant?.title) {
          variantTitleInput.value = variant.title;
        }
      });

      quoteForm.dataset.purchaseModeInitialized = "true";
    });
  };

  const initDimensionForms = (root = document) => {
    getScopedElements(root, "[data-product-dimension-form]").forEach((dimensionForm) => {
      if (dimensionForm.dataset.purchaseModeInitialized === "true") {
        return;
      }

      const productForm = dimensionForm.closest('form[is="product-form"]');
      const widthInput = dimensionForm.querySelector("[data-dimension-width]");
      const heightInput = dimensionForm.querySelector("[data-dimension-height]");
      const widthPropertyInput = dimensionForm.querySelector("[data-dimension-width-property]");
      const heightPropertyInput = dimensionForm.querySelector("[data-dimension-height-property]");
      const messageElement = dimensionForm.querySelector("[data-dimension-message]");
      const variantsElement = dimensionForm.querySelector("[data-dimension-variants]");
      const addToCartButton = productForm?.querySelector("[data-product-add-to-cart-button]");
      const idInput = productForm?.elements?.id;
      const productInfo = dimensionForm.closest(".product__info");
      const priceElement = productInfo?.querySelector("[data-dimension-price]");
      const comparePriceElement = productInfo?.querySelector("[data-dimension-compare-price]");
      const enterMessage = dimensionForm.dataset.enterMessage || "";
      const invalidMessage = dimensionForm.dataset.invalidMessage || "";
      const noMatchMessage = dimensionForm.dataset.noMatchMessage || "";
      const unavailableMessage = dimensionForm.dataset.unavailableMessage || "";
      const initialPriceText = priceElement?.textContent || "";
      const initialPriceHighlighted = priceElement?.classList.contains("price--highlight") ?? false;
      const initialComparePriceText = comparePriceElement?.textContent || "";
      const initialComparePriceHidden = comparePriceElement?.hidden ?? true;

      if (!productForm || !widthInput || !heightInput || !variantsElement || !idInput) {
        return;
      }

      let variants = [];

      try {
        variants = JSON.parse(variantsElement.textContent || "[]");
      } catch (error) {
        variants = [];
      }

      let matchedVariant = null;

      const setMessage = (message, state = "") => {
        if (!messageElement) {
          return;
        }

        if (!message) {
          messageElement.hidden = true;
          messageElement.textContent = "";
          messageElement.removeAttribute("data-state");
          return;
        }

        messageElement.hidden = false;
        messageElement.textContent = message;

        if (state) {
          messageElement.setAttribute("data-state", state);
        } else {
          messageElement.removeAttribute("data-state");
        }
      };

      const setButtonState = (enabled) => {
        if (!addToCartButton) {
          return;
        }

        addToCartButton.toggleAttribute("disabled", !enabled);
        addToCartButton.classList.toggle("button--ternary", !enabled);

        if (enabled) {
          addToCartButton.classList.add("button--primary");
          addToCartButton.classList.remove("button--secondary");
        } else {
          addToCartButton.classList.remove("button--primary");
          addToCartButton.classList.remove("button--secondary");
        }
      };

      const updatePrice = (variant) => {
        if (priceElement) {
          priceElement.textContent = formatVariantMoney(variant.price);
          priceElement.classList.toggle("price--highlight", Number(variant.compare_at_price) > Number(variant.price));
        }

        if (!comparePriceElement) {
          return;
        }

        if (Number(variant.compare_at_price) > Number(variant.price)) {
          comparePriceElement.hidden = false;
          comparePriceElement.textContent = formatVariantMoney(variant.compare_at_price);
        } else {
          comparePriceElement.hidden = true;
          comparePriceElement.textContent = "";
        }
      };

      const restoreInitialPrice = () => {
        if (priceElement) {
          priceElement.textContent = initialPriceText;
          priceElement.classList.toggle("price--highlight", initialPriceHighlighted);
        }

        if (comparePriceElement) {
          comparePriceElement.hidden = initialComparePriceHidden;
          comparePriceElement.textContent = initialComparePriceText;
        }
      };

      const findMatchingVariant = (width, height) =>
        variants.find((variant) => {
          const minWidth = parseNumber(variant.min_width_mm);
          const maxWidth = parseNumber(variant.max_width_mm);
          const minHeight = parseNumber(variant.min_height_mm);
          const maxHeight = parseNumber(variant.max_height_mm);

          if (![minWidth, maxWidth, minHeight, maxHeight].every(Number.isFinite)) {
            return false;
          }

          return width >= minWidth && width <= maxWidth && height >= minHeight && height <= maxHeight;
        }) || null;

      const syncProperties = () => {
        if (widthPropertyInput) {
          widthPropertyInput.value = widthInput.value.trim();
        }

        if (heightPropertyInput) {
          heightPropertyInput.value = heightInput.value.trim();
        }
      };

      const evaluate = () => {
        syncProperties();

        const rawWidth = widthInput.value.trim();
        const rawHeight = heightInput.value.trim();

        if (!rawWidth || !rawHeight) {
          matchedVariant = null;
          restoreInitialPrice();
          setButtonState(false);
          setMessage(enterMessage, "info");
          return;
        }

        const width = parseNumber(rawWidth);
        const height = parseNumber(rawHeight);

        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
          matchedVariant = null;
          restoreInitialPrice();
          setButtonState(false);
          setMessage(invalidMessage, "error");
          return;
        }

        const nextVariant = findMatchingVariant(width, height);

        if (!nextVariant) {
          matchedVariant = null;
          restoreInitialPrice();
          setButtonState(false);
          setMessage(noMatchMessage, "error");
          return;
        }

        matchedVariant = nextVariant;
        idInput.value = nextVariant.id;
        updatePrice(nextVariant);

        if (!nextVariant.available) {
          setButtonState(false);
          setMessage(unavailableMessage, "error");
          return;
        }

        setButtonState(true);
        setMessage("");
      };

      const handleSubmit = (event) => {
        evaluate();

        if (matchedVariant && matchedVariant.available) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!widthInput.value.trim()) {
          widthInput.reportValidity();
        } else if (!heightInput.value.trim()) {
          heightInput.reportValidity();
        }
      };

      widthInput.addEventListener("input", evaluate);
      heightInput.addEventListener("input", evaluate);
      productForm.addEventListener("submit", handleSubmit, true);

      setButtonState(false);
      setMessage(enterMessage, "info");
      evaluate();

      dimensionForm.dataset.purchaseModeInitialized = "true";
    });
  };

  const initPurchaseModeForms = (root = document) => {
    initQuoteForms(root);
    initDimensionForms(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initPurchaseModeForms(document));
  } else {
    initPurchaseModeForms(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initPurchaseModeForms(event.target);
  });
})();
